import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  FormHelperText,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useSelector } from "react-redux";
import {
  ALL_TICKET_REASONS,
  ALL_TICKET_STATUS_CODES,
  ApiConversation,
  ApiConversationAttachment,
  ApiTicketDetails,
  CALLBACK_REASONS,
  REOPEN_REASONS,
  STATUS_COLOR,
  Ticket,
  TicketStatus,
} from "./support.types";
import { mapCategory } from "./supportMappers";
import service from "../../services/api/service";
import { encrypt, encryptObject } from "../../utils/crypto";
import { useAppDispatch } from "../../services/redux/hooks";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import { qrcFlowTypeSelector, qrcEmailSelector } from "../../services/redux/slices/authSlice";
import { useAuth } from "../../hooks/useAuth";
import ModalPopup from "../../components/ui/ModalPopup";
import SelectOptions from "../../components/ui/SelectOptions";
import { TextareaField } from "../../components/ui/Inputs";
import { OutlinedPrimaryButton, SecondaryButtonText } from "../../components/ui/Button";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import Card from "../../components/ui/Card";
import raisedOnIcon from "../../assets/images/Raised_On.png";
import dueDateIcon from "../../assets/images/Due_Date.png";
import { ROUTES } from "../../routes/routerConfig";
import { Block } from "@mui/icons-material";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ticket-details returns the conversation a page at a time and reports the page's size as
// `convoCount`. "View more" is shown only while that equals a full page, since a short page is
// the last one.
const CONVERSATION_PAGE_SIZE = 30;

// ─── Request a Callback: phone + preferred slot ────────────────────────────────

// Country codes come from react-phone-input-2, which ships the full country + dial-code list
// offline — no master-data call and nothing hardcoded here. India is the default country.
const CALLBACK_DEFAULT_COUNTRY = "in";
const INDIA_DIAL_CODE = "91";

// react-phone-input-2 draws its own input, so these mirror SelectOptions' 38px / 11px controls.
const PHONE_INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  height: "38px",
  borderRadius: "6px",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  fontFamily: "Jost",
  fontSize: "11px",
  color: "#6f6b7d",
};
const PHONE_BUTTON_STYLE: React.CSSProperties = {
  borderRadius: "6px 0 0 6px",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  borderRight: "none",
  backgroundColor: "#fff",
};

// Callbacks are placed by a team working 10am–7pm IST, so slots run from 10:00 to the last one
// that still finishes by 7pm. Half-hour granularity keeps the list short enough to scan.
const CALLBACK_WINDOW_START_HOUR = 10;
const CALLBACK_WINDOW_END_HOUR = 19;
const CALLBACK_SLOT_MINUTES = 30;
const CALLBACK_TIME_ZONE = "Asia/Kolkata";

const formatSlotLabel = (minutesFromMidnight: number): string => {
  const hour24 = Math.floor(minutesFromMidnight / 60);
  const minute = minutesFromMidnight % 60;
  const suffix = hour24 < 12 ? "AM" : "PM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
};

const toSlotValue = (minutesFromMidnight: number): string =>
  `${String(Math.floor(minutesFromMidnight / 60)).padStart(2, "0")}:${String(
    minutesFromMidnight % 60
  ).padStart(2, "0")}`;

// 10:00 … 18:30 — the last slot starts early enough to be finished by 7pm.
const CALLBACK_SLOTS: { value: string; label: string; minutes: number }[] = (() => {
  const slots = [];
  const lastStart = CALLBACK_WINDOW_END_HOUR * 60 - CALLBACK_SLOT_MINUTES;
  for (let m = CALLBACK_WINDOW_START_HOUR * 60; m <= lastStart; m += CALLBACK_SLOT_MINUTES) {
    slots.push({ value: toSlotValue(m), label: formatSlotLabel(m), minutes: m });
  }
  return slots;
})();

// "Now" in IST regardless of the browser's own zone — a customer abroad must not be offered a
// slot that has already passed in India, nor be blocked from one that hasn't.
const nowInIst = (): { date: string; minutes: number } => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: CALLBACK_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  // Some ICU builds report midnight as hour "24" under hour12: false; %24 keeps 00:15 IST from
  // looking like minute 1440 and wrongly hiding every slot of the day.
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    minutes: (Number(get("hour")) % 24) * 60 + Number(get("minute")),
  };
};

// TextareaField is taller and larger-typed by default than SelectOptions' 38px / 11px select, so
// the phone and date inputs are pinned to match the dropdowns they sit beside.
const CALLBACK_FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    height: "38px",
    padding: "0 14px",
    fontSize: "11px",
  },
  "& .MuiOutlinedInput-input": {
    padding: 0,
    height: "100%",
    fontSize: "11px",
    color: "#6f6b7d",
    "&::placeholder": { color: "#a5a3ae", opacity: 1 },
  },
  // The native date picker's icon is oversized next to an 11px field.
  "& input[type=date]::-webkit-calendar-picker-indicator": {
    cursor: "pointer",
    opacity: 0.55,
    width: "14px",
    height: "14px",
  },
};

const CALLBACK_HELPER_SX = { mt: 0.5, ml: 0, fontSize: "11px" };

// Day arithmetic on the "YYYY-MM-DD" string only — parsing as UTC keeps the browser's own zone
// from shifting the result by a day.
const addDays = (isoDate: string, days: number): string =>
  new Date(new Date(`${isoDate}T00:00:00Z`).getTime() + days * 86400000)
    .toISOString()
    .slice(0, 10);

// A timestamp's calendar date in IST as "YYYY-MM-DD". createdAt comes back as a UTC instant, so
// a ticket raised at 05:00 IST (23:30 UTC the day before) must still count as raised today —
// reading the date off the raw string, or off the browser's own zone, shifts it by a day.
const istDateOf = (value: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() <= 1970) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: CALLBACK_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
};

// "2026-08-27" → "27 Aug 2026", for the line added to the conversation body.
const formatIstDate = (isoDate: string): string => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${day} ${MONTHS[Number(month) - 1] ?? month} ${year}`;
};

// Both notices that stand in for an unavailable Request a Callback close with the same
// "call us instead" sentence, so the two helpline numbers are written once here rather than
// drifting apart in two places.
const URGENT_ASSISTANCE_LINE = (
  <>
    If you need urgent assistance, please call us at <b>18002660200</b>, or{" "}
    <b>+91 22-6680-6464</b> if you are calling from abroad.
  </>
);

// Shared by those same two notices, which sit directly under (or in place of) the side-panel
// button and must line up with it.
const CALLBACK_NOTICE_SX = {
  display: "block",
  mt: 1,
  textAlign: "center",
  fontSize: "12px",
  color: "#64748B",
} as const;

// Shown after a Request a Callback submission. The API answers with a generic "The request was
// successful.", which doesn't tell the customer that someone will actually call them back.
const CALLBACK_SUCCESS_MESSAGE =
  "We've received your call request. Sit tight! We will reach out soon.";

// Submit Additional Details shares the same API call, so it needs its own copy. The heading goes
// in `title`, which replaces the popup's default "Success" (see DialogStatusPopup).
const ADDITIONAL_DETAILS_SUCCESS_TITLE = "Information received!";
const ADDITIONAL_DETAILS_SUCCESS_MESSAGE =
  "Thank you for sharing the required details. We will get back to you shortly.";

// Reopen Ticket goes through a different endpoint (update-ticket) and has its own copy too.
const REOPEN_SUCCESS_MESSAGE =
  "We’re re-evaluating your service request and will update you shortly. You can track progress " +
  "or upload additional details on this portal.";

// ticket-details returns a freeform status string (e.g. "In Progress") rather than the
// hyphenated TicketStatus union the rest of this page uses — normalize it.
const normalizeApiStatus = (status?: string | null): TicketStatus | undefined => {
  if (!status) return undefined;
  const trimmed = status.trim();
  if (trimmed === "In Progress") return "In-Progress";
  if (["In-Progress", "Pending Information", "Resolved", "Closed"].includes(trimmed)) {
    return trimmed as TicketStatus;
  }
  return undefined;
};

// Mirrors supportMappers.formatApiDate: an unset date from the CRM is null, and `new Date(null)`
// is the Unix epoch, so 1970 and earlier counts as "no date" rather than being rendered.
const formatApiDate = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getFullYear() <= 1970) return "";
  return (
    d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    ", " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }).toLowerCase()
  );
};

// ─── Vertical Timeline ────────────────────────────────────────────────────────

const VerticalTimeline: React.FC<{ raisedOn: string; dueDate: string }> = ({
  raisedOn,
  dueDate,
}) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* The asset carries its own tinted circle, so there's no wrapper to style */}
        <Box
          component="img"
          src={raisedOnIcon}
          alt=""
          sx={{ width: 36, height: 36, flexShrink: 0, display: "block" }}
        />
        <Box sx={{ width: 2, height: 28, bgcolor: "#CDE7E9" }} />
      </Box>
      <Box sx={{ pt: 0.5 }}>
        <Typography className="inside-page-row-lable">Raised On</Typography>
        <Typography className="inside-page-row-value">{raisedOn}</Typography>
      </Box>
    </Box>

    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        component="img"
        src={dueDateIcon}
        alt=""
        sx={{ width: 36, height: 36, flexShrink: 0, display: "block" }}
      />
      <Box sx={{ pt: 0.5 }}>
        <Typography className="inside-page-row-lable">Due Date</Typography>
        <Typography className="inside-page-row-value">{dueDate}</Typography>
      </Box>
    </Box>
  </Box>
);

// ─── File Chip with URL ───────────────────────────────────────────────────────

const handleFileDownload = async (url: string, name: string) => {
  // Fetch the file and convert it to a blob
  const response = await fetch(url);
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  
  // Create the link and trigger the download silently
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = name;
  
  document.body.appendChild(link);
  link.click();
  
  // Clean up instantly
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

// Strips the time portion off date strings like "6 Jul 2026, 05:25 pm" — used only on this page.
// Trims the time off a formatted date. A blank value means the CRM had no date at all (see
// formatApiDate), so it shows a dash rather than leaving an empty gap under the label.
const dateOnly = (value: string): string => value.split(",")[0]?.trim() || "-";

// Some fields come back carrying a placeholder rather than an empty value: a non-borrower ticket
// is keyed with the literal "N/A" (see RaiseARequest, which sends encrypt("N/A")), and a ticket
// that was never escalated can arrive with the string "null". None of those mean anything to a
// customer, so they are treated as absent and their field is hidden entirely.
const PLACEHOLDER_VALUES = new Set(["", "-", "n/a", "na", "null", "undefined"]);

const realValue = (value: string | null | undefined): string => {
  const trimmed = (value ?? "").trim();
  return PLACEHOLDER_VALUES.has(trimmed.toLowerCase()) ? "" : trimmed;
};

const FileChipUrl: React.FC<{ name: string; url: string }> = ({ name, url }) => (
  <Box
    sx={{
      // xs drops View/Download onto a second row: side by side they push the chip past the
      // message bubble, which is what made the thread scroll sideways.
      display: { xs: "flex", sm: "inline-flex" },
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "flex-start", sm: "center" },
      justifyContent: "space-between",
      gap: 1,
      px: 1.5,
      py: 1,
      border: "1px solid #e0e0e0",
      borderRadius: 2,
      bgcolor: "#fff",
      mr: { xs: 0, sm: 1 },
      mb: 1,
      minWidth: { xs: 0, sm: 160 },
      maxWidth: "100%",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, maxWidth: "100%" }}>
      <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: "#00B3B5", flexShrink: 0 }} />
      <Typography
        variant="body2"
        color="text.primary"
        noWrap
        sx={{ maxWidth: { xs: "100%", sm: 140 } }}
      >
        {name}
      </Typography>
    </Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 2, sm: 1 },
        flexShrink: 0,
        // Second row on xs — pulled level with the filename above it.
        pl: { xs: 3.5, sm: 0 },
      }}
    >
      <Box
        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "#00B3B5",
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={600} color="#00B3B5">
          View
        </Typography>
      </Box>
      <Box
        onClick={() => handleFileDownload(url, name)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "#00B3B5",
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" fontWeight={600} color="#00B3B5">
          Download
        </Typography>
      </Box>
    </Box>
  </Box>
);

// ─── Section label ────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ label: string }> = ({ label }) => (
  <Typography className="inside-page-row-lable" sx={{ display: "block", mb: 0.5 }}>
    {label}
  </Typography>
);

// ─── Thread messages (shared between the current ticket and linked ancestor tickets) ──

const ThreadMessages: React.FC<{ conversations: ApiConversation[] }> = ({ conversations }) => (
  <div className="td-thread__messages">
    {conversations.map((msg, i) => {
      const isUser = msg.sender === "user";
      const role = isUser ? "customer" : "support";
      // Support senders may carry an escalation level (e.g. "L2") → "Avanse Support (L2)".
      const roleLabel = isUser
        ? "Customer"
        : /^L\d+$/i.test(msg.sender)
        ? `Avanse Support (${msg.sender.toUpperCase()})`
        : "Avanse Support";
      // Callback / Reopen messages start with a known tag line — render it as an inline
      // badge and keep the reason (dropdown value) on the same line.
      const TAG_LABELS: Record<string, string> = {
        "Request a Callback": "Callback Requested",
        "Ticket Re-opened": "Ticket Re-opened",
        "Reopen Ticket": "Ticket Re-opened",
      };
      const rawText = msg.text ?? "";
      const matchedTag = isUser
        ? Object.keys(TAG_LABELS).find((t) => rawText.trimStart().startsWith(t))
        : undefined;
      const badgeLabel = matchedTag ? TAG_LABELS[matchedTag] : "";
      const bodyText = matchedTag
        ? rawText.trimStart().slice(matchedTag.length).replace(/^\s+/, "")
        : rawText;
      // Both a tagged message and the ticket's own description lead with the Reason / Rationale
      // the customer picked from a dropdown, followed by their Additional Context — the reason
      // is shown in bold with the context underneath. A tagged message always has one (the tag
      // is followed by the reason). An untagged one only counts as having one when its first
      // line is a known dropdown value, so a plain free-text description — which can also run
      // to several lines — never gets its opening line emphasised.
      const firstBreakIndex = bodyText.indexOf("\n");
      const firstLine = firstBreakIndex === -1 ? bodyText : bodyText.slice(0, firstBreakIndex);
      const hasReasonLine =
        !!matchedTag || (isUser && ALL_TICKET_REASONS.includes(firstLine.trim()));
      const reasonLine = hasReasonLine ? firstLine : "";
      const contextText = !hasReasonLine
        ? bodyText
        : firstBreakIndex === -1
        ? ""
        : bodyText.slice(firstBreakIndex + 1).replace(/^\s+/, "");
      return (
        <div key={i} className={`td-thread__msg td-thread__msg--${role}`}>
          <div className="td-thread__msg-left">
            <span className={`td-thread__avatar td-thread__avatar--${role}`}>
              {isUser ? "C" : "A"}
            </span>
            {i < conversations.length - 1 && (
              <div className="td-thread__connector" />
            )}
          </div>
          <div className="td-thread__msg-right">
            <div className="td-thread__msg-meta">
              <span className={`td-thread__role td-thread__role--${role}`}>
                {roleLabel}
              </span>
              <span className="td-thread__date">{dateOnly(formatApiDate(msg.createdAt))}</span>
            </div>
            <div className="td-thread__bubble">
              {matchedTag && (
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    px: 1,
                    py: 0.4,
                    mr: 1,
                    borderRadius: 1,
                    bgcolor: "#e6f7f7",
                    color: "#00838a",
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    verticalAlign: "middle",
                  }}
                >
                  {badgeLabel}
                </Box>
              )}
              {reasonLine && (
                /* Inline so a tagged message keeps its reason on the badge's line */
                <span className="td-thread__text td-thread__text--reason">{reasonLine}</span>
              )}
              {contextText && (
                <div className={`td-thread__text${reasonLine ? " td-thread__text--context" : ""}`}>
                  {contextText}
                </div>
              )}
              {msg.attachments.length > 0 && (
                <Box sx={{ mt: 0.75 }}>
                  {msg.attachments.map((att: ApiConversationAttachment) => (
                    <FileChipUrl key={att.name} name={att.name} url={att.url} />
                  ))}
                </Box>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

// ─── Thread box: collapsible header + messages ────────────────────────────────
// Used both for the current ticket's own "Conversation History" and for the
// linked L1/L2 ancestor tickets shown above it when this ticket was escalated.

const ThreadBox: React.FC<{
  refLabel?: string;
  title: string;
  conversations: ApiConversation[];
  defaultExpanded?: boolean;
  collapsible?: boolean;
}> = ({ refLabel, title, conversations, defaultExpanded = true, collapsible = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // When not collapsible (e.g. the current ticket's "Conversation History") the thread
  // is always shown and the header has no toggle.
  const isOpen = collapsible ? expanded : true;

  if (!conversations.length) return null;

  return (
    <div className={`td-thread${isOpen ? " td-thread--open" : ""}`}>
      <div
        className="td-thread__header"
        style={collapsible ? undefined : { cursor: "default" }}
        {...(collapsible
          ? {
              role: "button",
              tabIndex: 0,
              onClick: () => setExpanded((p) => !p),
              onKeyDown: (e: React.KeyboardEvent) => e.key === "Enter" && setExpanded((p) => !p),
            }
          : {})}
      >
        <div className="td-thread__header-left">
          <InsertLinkIcon className="td-thread__link-icon" />
          {refLabel && (
            <>
              <span className="td-thread__ref">{refLabel}</span>
              <span className="td-thread__sep">|</span>
            </>
          )}
          <span className="td-thread__title">{title}</span>
        </div>
        {collapsible && (
          <button className="td-thread__toggle-btn" type="button">
            {expanded ? "Hide" : "View Thread"}
            {expanded
              ? <KeyboardArrowUpIcon className="td-thread__toggle-icon" />
              : <KeyboardArrowDownIcon className="td-thread__toggle-icon" />}
          </button>
        )}
      </div>

      <Collapse in={isOpen} unmountOnExit>
        <ThreadMessages conversations={conversations} />
      </Collapse>
    </div>
  );
};

// ─── TicketDetails ────────────────────────────────────────────────────────────

const TicketDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { ticketNumber } = useParams<{ ticketNumber: string }>();
  const qrcFlowType = useSelector(qrcFlowTypeSelector);
  const qrcEmail = useSelector(qrcEmailSelector);
  const { isAuthenticated } = useAuth();
  // A logged-in session always wins over stale QRC state left in this tab by an earlier pre-login
  // journey — otherwise the pre-login treatment leaks onto post-login screens.
  const isQrcFlow = !!qrcFlowType && !isAuthenticated;

  // Which of the copyable header values (LAN / ticket number) was just copied.
  const [copiedField, setCopiedField] = useState<"lan" | "ticket" | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState<"callback" | "reopen" | "additionalDetails" | null>(null);
  const [reason, setReason] = useState("");
  const [context, setContext] = useState("");
  // Request a Callback only: who to call, and when (IST working hours).
  // callbackPhone is the full number react-phone-input-2 reports, dial code included ("919876…").
  const [callbackPhone, setCallbackPhone] = useState("");
  const [callbackDialCode, setCallbackDialCode] = useState(INDIA_DIAL_CODE);
  const [callbackDate, setCallbackDate] = useState("");
  const [callbackSlot, setCallbackSlot] = useState("");
  const [callbackFiles, setCallbackFiles] = useState<{ file: File; objectUrl: string }[]>([]);
  const [fileError, setFileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [apiDetails, setApiDetails] = useState<ApiTicketDetails | null>(null);
  const [ancestorTickets, setAncestorTickets] = useState<ApiTicketDetails[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  // Appending a page must not trip the section-level spinner (see detailsLoading above).
  const [loadingMoreConversations, setLoadingMoreConversations] = useState(false);
  // Which conversation page has been loaded, and whether the last one was full (convoCount).
  const [conversationPage, setConversationPage] = useState(1);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const CB_MAX_SIZE_MB = 10;
  const ACCEPTED_MIME = ["application/pdf", "image/png", "image/jpeg"];

  const minCharacters = 20;
  // Request a Callback → 200 chars; Re-open / Submit additional details → 500 chars.
  const maxCharacters = modalMode === "callback" ? 200 : 500;
  // Measured on the trimmed value: counting whitespace let a box of nothing but spaces satisfy
  // the minimum and submit. trimmedContext is also what gets sent, so no padding reaches the API.
  const trimmedContext = context.trim();
  const isContextValid =
    trimmedContext.length >= minCharacters && trimmedContext.length <= maxCharacters;
  // Shared by all three modes (callback / reopen / additional details), since they reuse this
  // one Additional Context box. Like the phone field below, it stays quiet until something has
  // actually been typed — an untouched box shouldn't be scolded — but once there is text too
  // short to submit, the reason is spelled out instead of leaving Submit disabled in silence.
  // Whitespace-only input counts as typed, otherwise a box of spaces disables Submit mutely.
  const contextError =
    context.length > 0 && trimmedContext.length < minCharacters
      ? `Minimum ${minCharacters} characters required`
      : "";

  // ── Request a Callback: phone + preferred slot ──
  const isCallbackMode = modalMode === "callback";
  const ist = nowInIst();
  // The component's value carries the dial code, so validation runs on the national part alone.
  const fullPhoneDigits = callbackPhone.replace(/\D/g, "");
  const nationalPhone = fullPhoneDigits.startsWith(callbackDialCode)
    ? fullPhoneDigits.slice(callbackDialCode.length)
    : fullPhoneDigits;
  const isIndianNumber = callbackDialCode === INDIA_DIAL_CODE;
  // 10 digits for India; other countries vary, so only a sane 7–15 range is enforced there.
  const isPhoneValid = isIndianNumber
    ? /^[6-9]\d{9}$/.test(nationalPhone)
    : /^\d{7,15}$/.test(nationalPhone);
  // The team never places the call on the day it is asked for, so the earliest bookable day is
  // tomorrow in IST — which also means no chosen slot can already have passed, and the full
  // 10:00–18:30 list stays on offer whatever time of day the customer opens the form.
  const minCallbackDate = addDays(ist.date, 1);
  const isCallbackScheduleValid =
    !!callbackDate && callbackDate >= minCallbackDate && !!callbackSlot;
  // Only the callback flow has these fields, so the other two modes are unaffected.
  const isCallbackDetailsValid = !isCallbackMode || (isPhoneValid && isCallbackScheduleValid);

  const reasonOptions = (modalMode === "reopen" ? REOPEN_REASONS : CALLBACK_REASONS).map(
    (reason) => ({ value: reason, label: reason })
  );

  const locationState = location.state as { ticket?: Ticket; chatMode?: boolean } | null;
  const chatMode = !!locationState?.chatMode;
  const ticket: Ticket | undefined = locationState?.ticket;

  // page 1 is the initial load; "View more" asks for the next page and appends what comes back.
  const fetchTicketDetails = useCallback(
    (page: number = 1) => {
      if (!ticketNumber) return;
      const reqId = ticket?.referenceId;
      if (!reqId) return;
      const isFirstPage = page === 1;
      // detailsLoading swaps the whole conversation section for a spinner, so appending must not
      // set it — doing so unmounted the thread and read as a full page refresh.
      if (isFirstPage) setDetailsLoading(true);
      else setLoadingMoreConversations(true);
      service.support
        .getTicketDetails(ticketNumber, reqId, page)
        .then((res) => {
          const list: ApiTicketDetails[] = res?.data?.data ?? [];
          // TEMP(remove-with-hardcode): supportService.getTicketDetails currently hardcodes
          // ticket 38221 / its reqId for testing the conversation paging, so the response never
          // matches this route's ticket number and `match` came back null — which is why "View
          // more" never appeared. Falling back to the first entry lets the hardcoded ticket
          // render. Delete this fallback at the same time as that hardcode.
          const match =
            list.find((d) => String(d.ticketNumber) === String(ticketNumber)) ?? list[0] ?? null;
          const batch = match?.conversations ?? [];

          if (isFirstPage) {
            setApiDetails(match);
          } else {
            // Later pages carry the same ticket with the next slice of the conversation, so keep
            // the page-1 header fields and only grow the message list.
            setApiDetails((prev) =>
              prev ? { ...prev, conversations: [...prev.conversations, ...batch] } : match
            );
          }
          // Only a full page means another may follow. Read off the ticket object, falling back to
          // the response root, since either is a reasonable place for the API to put it. Absent
          // (as today) → no "View more" at all.
          const convoCount =
            match?.convoCount ?? (res?.data as { convoCount?: number } | undefined)?.convoCount;
          setHasMoreConversations(convoCount === CONVERSATION_PAGE_SIZE);
          setConversationPage(page);

          if (!isFirstPage) return;
          // escalatedFrom === null means this ticket is the original (L1); a non-null
          // value is the parent ticket number, so walk it up to collect L1..Ln-1 ancestors.
          const chain: ApiTicketDetails[] = [];
          const seen = new Set<string>([String(ticketNumber)]);
          let cursor = match;
          while (cursor?.escalatedFrom && !seen.has(String(cursor.escalatedFrom))) {
            const parent = list.find((d) => String(d.ticketNumber) === String(cursor?.escalatedFrom)) ?? null;
            if (!parent) break;
            chain.unshift(parent);
            seen.add(String(parent.ticketNumber));
            cursor = parent;
          }
          setAncestorTickets(chain);
        })
        .catch(() => {
          // silently fall back to listing state data
        })
        .finally(() => {
          if (isFirstPage) setDetailsLoading(false);
          else setLoadingMoreConversations(false);
        });
    },
    [ticketNumber, ticket?.referenceId]
  );

  const handleViewMoreConversations = () => {
    if (loadingMoreConversations) return;
    fetchTicketDetails(conversationPage + 1);
  };

  useEffect(() => {
    fetchTicketDetails(1);
  }, [fetchTicketDetails]);

  // ticket-listing with refresh=true is what re-syncs this customer's tickets from the CRM, so
  // it's pinged after a successful re-open / additional-details / callback submit: the listing
  // the user goes back to then already shows the updated status. Same payload shape as the
  // listing's own Refresh button (see ViewTickets) — email-keyed for the non-borrower QRC flow,
  // LAN-keyed otherwise. Best-effort: a failure here must not affect the submit result.
  const refreshTicketListing = useCallback(async () => {
    const lan = ticket?.loanAccountNumber ?? "";
    const listingPayload =
      qrcFlowType === "nonBorrower"
        ? qrcEmail
          ? { email: qrcEmail, status: ALL_TICKET_STATUS_CODES }
          : null
        : lan
        ? { lan: [lan], status: ALL_TICKET_STATUS_CODES }
        : null;
    if (!listingPayload) return;
    try {
      await service.support.getTicketListing({ data: encryptObject(listingPayload) }, 1, 5, true);
    } catch {
      // Ignore — the submit itself already succeeded.
    }
  }, [apiDetails?.subject, ticket?.loanAccountNumber, qrcFlowType, qrcEmail]);

  const handleCopy = (value: string, field: "lan" | "ticket") => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setOpenModal(false);
    setModalMode(null);
    setReason("");
    setContext("");
    setCallbackPhone("");
    setCallbackDialCode(INDIA_DIAL_CODE);
    setCallbackDate("");
    setCallbackSlot("");
    callbackFiles.forEach(({ objectUrl }) => URL.revokeObjectURL(objectUrl));
    setCallbackFiles([]);
    setFileError("");
    setSubmitError("");
  };

  const handleAddCallbackFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const files = Array.from(incoming);
    const additions: { file: File; objectUrl: string }[] = [];
    let error = "";
    files.forEach((file) => {
      if (!ACCEPTED_MIME.includes(file.type)) { error = `"${file.name}": only PDF, JPG, PNG allowed.`; return; }
      if (file.size > CB_MAX_SIZE_MB * 1024 * 1024) { error = `"${file.name}": exceeds ${CB_MAX_SIZE_MB} MB limit.`; return; }
      additions.push({ file, objectUrl: URL.createObjectURL(file) });
    });
    setFileError(error);
    if (additions.length) setCallbackFiles((prev) => [...prev, ...additions]);
  };

  const handleRemoveCallbackFile = (index: number) => {
    setCallbackFiles((prev) => {
      URL.revokeObjectURL(prev[index].objectUrl);
      return prev.filter((_, i) => i !== index);
    });
    setFileError("");
  };

  const handleCallbackSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isContextValid || !isCallbackDetailsValid || !ticket) return;
    setSubmitError("");
    setSubmitting(true);
    try {
      if (modalMode === "reopen") {
        // Reopen goes through the JSON update-ticket API (type "cl"); each field is
        // individually encrypted, matching the payload the backend expects.
        // Tag the reason with a "Reopen Ticket" line (like "Request a Callback") so the
        // reopened ticket's details show a Reopen Ticket badge.
        const reopenReason = `Ticket Re-opened\n${reason}`;
        const reopenPayload = {
          description: encrypt(trimmedContext),
          reason: encrypt(reopenReason),
          ticketId: encrypt(ticket.ticketNumber),
          requesterId: encrypt(ticket.referenceId || ""),
        };
        await service.support.updateTicket(reopenPayload);
        handleClose();
        // Refresh the ticket details so the updated status/conversation shows immediately,
        // and re-sync the listing so it reflects the re-opened ticket too.
        fetchTicketDetails();
        await refreshTicketListing();
        // Own copy rather than the API's generic success text (after the reopen popup closes).
        dispatch(
          setStatusPopup({
            status: "success",
            message: REOPEN_SUCCESS_MESSAGE,
          })
        );
        return;
      }

      const userId = ticket.referenceId || "";
      const formData = new FormData();
      // For "Request a Callback", tag the message with a "Request a Callback" line above it.
      const reasonText = modalMode === "callback" ? `Request a Callback\n${reason}` : reason;
      // Just what the customer typed. The number and the preferred slot used to be prefixed onto
      // this value for want of anywhere else to put them; they travel as their own fields below.
      formData.append("body", encrypt(trimmedContext));
      formData.append("userId", encrypt(userId));
      formData.append("reason", encrypt(reasonText));
      formData.append("ticketId", encrypt(ticket.ticketNumber));
      // Request a Callback (In-Progress / Pending Information tickets) sends type "cl", plus the
      // three fields only this mode collects. Sent in machine-readable form rather than as the
      // labels on screen — "2026-10-02" and "10:30" (IST, 24-hour, the slot's start) — so the
      // CRM can act on them without parsing "02 Oct 2026" or guessing at AM/PM. The number
      // carries its dial code, which is the whole of what the country picker contributes.
      if (isCallbackMode) {
        formData.append("type", encrypt("cl"));
        formData.append("phoneNumber", encrypt(`+${fullPhoneDigits}`));
        formData.append("preferredCallingDate", encrypt(callbackDate));
        formData.append("preferredCallingTime", encrypt(callbackSlot));
      }
      callbackFiles.forEach(({ file }) => formData.append("attachments", file));
      const urgentRes = await service.support.urgentRequests(formData);
      handleClose();
      // Refresh the ticket details so the new callback / additional-details reflects immediately,
      // and re-sync the listing so it reflects the updated ticket too.
      fetchTicketDetails();
      await refreshTicketListing();
      // Reopen returned earlier, so this is either Request a Callback or Submit Additional
      // Details — each gets its own copy instead of the API's generic success text.
      dispatch(
        setStatusPopup({
          status: "success",
          ...(modalMode === "callback"
            ? { message: CALLBACK_SUCCESS_MESSAGE }
            : {
                title: ADDITIONAL_DETAILS_SUCCESS_TITLE,
                message: ADDITIONAL_DETAILS_SUCCESS_MESSAGE,
              }),
        })
      );
    } catch (error: any) {
      // Show the error message returned by the API.
      setSubmitError(error?.response?.data?.message ?? "");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = event.target.value;
    if (val.length <= maxCharacters) setContext(val);
  };

  if (!ticket) {
    return (
      <Box sx={{ maxWidth: 880, mx: "auto", px: 3, py: 6, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          Ticket not found.
        </Typography>
        <SecondaryButtonText
          onClick={() => navigate(ROUTES.ContactUs.viewTickets.locationPath)}
          sx={{ mt: 2 }}
        >
          Go back
        </SecondaryButtonText>
      </Box>
    );
  }

  // Prefer the freshly-fetched ticket-details status over the possibly-stale one
  // passed via navigation state from the ticket listing.
  const displayStatus: TicketStatus = normalizeApiStatus(apiDetails?.status) ?? ticket.status;
  const statusColor = STATUS_COLOR[displayStatus];
  // The listing-mapped ticket comes first so this page and the listing card agree, but it can
  // arrive without a typeOfTicket — that is why the title was blank until the listing had been
  // refreshed and re-entered. This ticket's own details response carries the value all along,
  // in CrmType ("Fees and Charges"); its `type` is the Query / Complaint bucket shown under
  // Category, so it is deliberately not used here. `||`, not `??`, so "" falls through too.
  const displayTypeOfTicket = ticket.typeOfTicket || apiDetails?.CrmType || "";

  // The bucket as the API worded it ("Service Request"), same as the listing card shows;
  // `category` is the normalized Complaint/Request/Query enum, kept only as a fallback.
  const displayCategory = ticket.subCategory ?? ticket.category;
  // The "No, I don't" journey is a new customer with no loan account at all, so this field is
  // dropped for that flow outright, whatever the API happens to return. Otherwise it is "" only
  // when there is no genuine number — including the literal "N/A" such a ticket is keyed with —
  // so the field below is left out rather than showing a placeholder. Display only: nothing that
  // gets submitted reads this.
  const isNonBorrowerFlow = qrcFlowType === "nonBorrower" && !isAuthenticated;
  const displayLoanAccountNumber = isNonBorrowerFlow
    ? ""
    : realValue(apiDetails?.accountNumber ?? ticket.loanAccountNumber);
  // Same treatment for the escalation chip, which was rendering "Escalated against ticket ID: null"
  // whenever the API sent the string "null" for a ticket that was never escalated.
  const escalatedFromTicket = realValue(ticket.escalatedFrom);
  // ticket-details reuses `subject` for two different things: on a borrower's ticket it repeats
  // accountNumber (the LAN), on a non-borrower's it carries the concern they picked on Raise a
  // Request. Only the second is a title worth showing above "Type of Ticket", so the heading is
  // limited to the new-customer ("No, I don't") journey outright — an existing customer, whether
  // QRC-verified as a borrower, upgraded into that flow, or logged in, already has the LAN in
  // its own field below and never needs it repeated as a title.
  const apiSubject = (apiDetails?.subject ?? "").trim();
  // Kept as a second guard for the non-borrower case, so a details response that echoes the
  // account number anyway (or the literal "N/A" such a ticket is keyed with) still doesn't
  // surface as a heading. This is a *contains* test, not an equality one: the CRM composes the
  // subject as "<Type> - <LAN>" ("Balance Principle - LUWCE01000545"), which a strict === let
  // straight through even though the whole point is that it only repeats the LAN and the type
  // already shown below. Guarded on length so a short or placeholder account value can't match
  // half the subjects on the page.
  const subjectLower = apiSubject.toLowerCase();
  const subjectIsAccountNumber = [apiDetails?.accountNumber, ticket.loanAccountNumber]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase())
    .some((value) => value.length >= 4 && subjectLower.includes(value));
  const displaySubject = isNonBorrowerFlow && !subjectIsAccountNumber ? apiSubject : "";
  const displayDescription = apiDetails?.description ?? ticket.description;
  const displayConversations: ApiConversation[] = apiDetails?.conversations ?? [];
  // The current ticket's description is shown as the first Customer message in the
  // Conversation History, carrying the customer's own uploaded documents (ticket-level
  // "attachemnts"). Any "Ticket Re-opened" / "Request a Callback" tag is rendered as a
  // badge by ThreadMessages.
  const conversationHistory: ApiConversation[] = displayDescription
    ? [
        {
          sender: "user",
          text: displayDescription,
          attachments: apiDetails?.attachemnts ?? [],
          createdAt: apiDetails?.createdAt ?? ticket.raisedOnIso ?? "",
        },
        ...displayConversations,
      ]
    : displayConversations;
  // "Attachments" section shows ONLY documents Avanse (support) shared in the conversation
  // (sender !== "user"). The customer's own documents live inline with their messages.
  // Hidden when there are no Avanse documents.
  const sectionAttachments: ApiConversationAttachment[] = displayConversations
    .filter((c) => c.sender !== "user")
    .flatMap((c) => c.attachments ?? []);
  // Set when this ticket was escalated to a new complaint ticket (note the API's "esclatedTo" spelling).
  const escalatedTo = apiDetails?.esclatedTo ?? ticket.escalatedTo;

  // ── Request a Callback: locked until T+1 ──
  // Support needs the raising day itself to work the ticket, so the action only opens the day
  // after it was raised — before that the button is rendered disabled rather than hidden, with
  // the unlock date spelled out beneath it. Both sides are IST calendar dates (see istDateOf),
  // so a customer abroad sees the same unlock moment as the team placing the call. When neither
  // createdAt nor the listing's raisedOnIso is usable the gate is left open, since locking the
  // action on a date the API never sent would strand the ticket with no way to ask for a call.
  const ticketCreatedIstDate = istDateOf(apiDetails?.createdAt ?? ticket.raisedOnIso ?? "");
  const callbackUnlockDate = ticketCreatedIstDate ? addDays(ticketCreatedIstDate, 1) : "";
  const isCallbackUnlocked = !callbackUnlockDate || ist.date >= callbackUnlockDate;

  // ── Request a Callback: Query and Request only ──
  // A callback is a service channel for questions and service requests. A complaint is worked
  // through its own thread instead, so the action is replaced by the helpline notice below —
  // that also covers escalation tickets, which the CRM files as complaints against the ticket
  // they were raised from. mapCategory reads the same bucket the "Category" field above shows,
  // so what the customer is told and what the button does can never disagree; a bucket the CRM
  // left unset falls back to Query, leaving the action available rather than stranding a ticket
  // with no way to ask for a call.
  const isCallbackAllowedForCategory = mapCategory(displayCategory) !== "Complaint";

  const breadcrumbItems = [
    { label: "Dashboard", path: "/" },
    { label: "Existing Tickets", path: ROUTES.ContactUs.viewTickets.locationPath },
    { label: "Ticket Details" },
  ];

  return (
    <Box>
      {!isQrcFlow && <Breadcrumbs items={breadcrumbItems} />}

      <Grid container>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
            <IconButton
              size="small"
              aria-label="back"
              // Goes to the ticket listing rather than navigate(-1): this page is also opened from
              // the Lodge a Complaint "Pending Information" prompt, and a history pop sent those
              // users back into the complaint form. The listing is this page's parent either way —
              // it's what the breadcrumb above already claims.
              onClick={() => navigate(ROUTES.ContactUs.viewTickets.locationPath)}
              sx={{
                mr: 1,
                width: 34,
                height: 34,
                borderRadius: "8px",
                bgcolor: "#F7F8FA",
                color: "#475569",
                "&:hover": { bgcolor: "#EDEFF3" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Typography variant="h6" sx={{ color: "#168794", fontSize: "15px", fontWeight: "600" }}>
              Ticket Details
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={3} alignItems="flex-start">
        {/* ── Left column ── */}
        <Grid item xs={12} md={8}>
          {/* Escalated ticket notice — full-width cream banner above the ticket card */}
          {escalatedTo && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 2,
                py: 1.25,
                mb: 2,
                borderRadius: 2,
                bgcolor: "#FCF6E3",
              }}
            >
              <InfoOutlinedIcon sx={{ color: "#EAB308", fontSize: 20, flexShrink: 0 }} />
              {/* Reads the ticket's own status rather than assuming an escalated ticket was
                  closed: once escalating an active ticket only marks it Escalated — leaving its
                  closing date alone — this banner would otherwise tell a customer their
                  In-Progress ticket had been closed. */}
              <Typography sx={{ fontSize: "0.875rem", color: "#3A3A3A", lineHeight: 1.5 }}>
                {displayStatus === "Closed"
                  ? `This ticket has been closed and new complaint ticket has been raised with ticket ID: ${escalatedTo}`
                  : `A new complaint ticket has been raised against this ticket with ticket ID: ${escalatedTo}`}
              </Typography>
            </Box>
          )}
          {/* padding="0px" so the header band can run edge-to-edge; the body below adds its own padding */}
          <Card padding="0px">
            {/* Ticket header — mint band across the top of the card */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                px: { xs: 1.25, sm: 3 },
                py: { xs: 1.5, sm: 2 },
                bgcolor: "#E7F5F6",
                borderRadius: "6px 6px 0 0",
              }}
            >
              <Box>
                {displaySubject && (
                  <Typography
                    className="inside-page-row-value"
                    style={{ color: "#168794", fontWeight: 600, fontSize: "18px", marginBottom: "4px" }}
                  >
                    {displaySubject}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5, flexWrap: "wrap" }}>
                  <SectionLabel label="Type of Ticket" />

                  {escalatedFromTicket && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1,
                        py: 0.25,
                        mb: 0.5,
                        bgcolor: "#fff",
                        border: "1.5px solid #00B3B5",
                        borderRadius: 1,
                        color: "#00B3B5",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                    >
                      <LocalOfferOutlinedIcon sx={{ fontSize: 13 }} />
                      Escalated against ticket ID: {escalatedFromTicket}
                    </Box>
                  )}
                </Box>

                {/* Always rendered, even with nothing in it, so the label above always has a row */}
                <Typography variant="h5" fontWeight={800} color="text.primary">
                  {displayTypeOfTicket}
                </Typography>
              </Box>

              <Chip
                label={displayStatus}
                size="small"
                sx={{
                  bgcolor: "#fff",
                  color: statusColor,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 28,
                  borderRadius: 2,
                  border: `1.5px solid ${statusColor}`,
                  ml: 2,
                  mt: 0.5,
                  "& .MuiChip-label": { px: 1.5 },
                }}
              />
            </Box>

            <Box sx={{ px: { xs: 1.25, sm: 3 }, pt: 2.5, pb: { xs: 1.25, sm: 3 } }}>
            {/* Loan Account Number + Category + Ticket Number */}
            <Box
            className="view-tickets-container"
              sx={{
                display: "grid",
                // Two-up on xs, as on the ViewTickets cards — single-column stacked these three
                // short values into three tall rows.
                gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
                gap: { xs: 2, sm: 3 },
                pb: 2.5,
                mb: 2.5,
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              {/* Dropped entirely when there is no account number — a "No, I don't" visitor is a
                  new customer with no loan account, and its ticket is keyed with the literal
                  "N/A", which is not worth a labelled row. The grid reflows around it, as it
                  already does for Category below. */}
              {displayLoanAccountNumber && (
                <Box>
                  <SectionLabel label="Loan Account Number" />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography className="inside-page-row-value">
                      {displayLoanAccountNumber}
                    </Typography>
                    <Tooltip title={copiedField === "lan" ? "Copied!" : "Copy"} placement="top">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(displayLoanAccountNumber, "lan")}
                        sx={{ p: 0.25 }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
              {displayCategory && (
                <Box>
                  <SectionLabel label="Category" />
                  {/* displayCategory prefers this ticket's own CrmType/category over the
                      listing's normalized guess, which was all that showed here before. */}
                  <Typography className="inside-page-row-value">{displayCategory}</Typography>
                </Box>
              )}
              <Box>
                <SectionLabel label="Ticket Number" />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography className="inside-page-row-value">{ticket.ticketNumber}</Typography>
                  <Tooltip title={copiedField === "ticket" ? "Copied!" : "Copy"} placement="top">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(ticket.ticketNumber, "ticket")}
                      sx={{ p: 0.25 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
            {/* Linked ancestor tickets (L1, L2, ...) — only shown when this ticket was escalated */}
            {ancestorTickets.length > 0 && (
              <Box sx={{ mb: 2.5 }}>
                <SectionLabel label={"Previous Ticket"} />
                {ancestorTickets.map((anc, i) => (
                  <ThreadBox
                    key={anc.ticketNumber}
                    refLabel={String(anc.ticketNumber)}
                    title={`${anc.category}`}
                    conversations={
                      anc.description
                        ? [
                            {
                              sender: "user",
                              text: anc.description,
                              // Include the previous ticket's own documents (API spells it "attachemnts").
                              attachments: anc.attachemnts ?? [],
                              createdAt: anc.createdAt,
                            },
                            ...anc.conversations,
                          ]
                        : anc.conversations
                    }
                    defaultExpanded={false}
                  />
                ))}
              </Box>
            )}

            {/* Conversation History — starts with the current ticket's own description message */}
            {detailsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} sx={{ color: "#00B3B5" }} />
              </Box>
            ) : (
              conversationHistory.length > 0 && (
                <>
                  <SectionLabel
                  label={"Current Ticket Details"}
                />
                  <Box sx={{ mb: 2.5 }}>
                  <ThreadBox title="Conversation" conversations={conversationHistory} defaultExpanded collapsible={false} />
                  {/* Pages the conversation: each click asks ticket-details for the next page and
                      appends it. Hidden once a page comes back empty, since that's the only
                      end-of-list signal the response gives us. */}
                  {hasMoreConversations && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 1.5 }}>
                      <SecondaryButtonText
                        onClick={handleViewMoreConversations}
                        disabled={loadingMoreConversations}
                      >
                        {loadingMoreConversations ? "Loading…" : "View more"}
                      </SecondaryButtonText>
                    </Box>
                  )}
                </Box>
                </>
              
              )
            )}

            {/* Attachments — only documents Avanse (support) shared in the conversation */}
            {sectionAttachments.length > 0 && (
              <Box>
                <SectionLabel label="Attachments" />
                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                  {sectionAttachments.map((att, i) => (
                    <FileChipUrl key={`${att.name}-${i}`} name={att.name} url={att.url} />
                  ))}
                </Box>
              </Box>
            )}
            </Box>
          </Card>
        </Grid>

        {/* ── Right column ── */}
        <Grid item xs={12} md={4}>
          <Card marginBottom="16px">
            <Typography className="inside-page-row-lable" sx={{ display: "block", mb: 2 }}>
              Timeline Estimate
            </Typography>
            <VerticalTimeline raisedOn={dateOnly(ticket.raisedOn)} dueDate={dateOnly(ticket.dueDate)} />

            {ticket.statusMessage && (
              <Box sx={{ mt: 2.5, px: 2, py: 1.5, borderRadius: 2, bgcolor: "#FFF3E8" }}>
                <Typography variant="body2" fontWeight={500} color="#D97706" sx={{ textAlign: "center" }}>
                  {ticket.statusMessage}
                </Typography>
              </Box>
            )}
          </Card>

          {/* Side-panel actions — all three share the project's outlined button, stacked full
              width. Note width="100%" rather than fullWidth: OutlinedPrimaryButton sets
              `width: width ?? "auto"`, which overrides MUI's fullWidth and leaves the buttons
              inline. textTransform is reset because the component capitalises every word
              ("Request A Callback"). */}
          {displayStatus === "Resolved" && (
            <OutlinedPrimaryButton
              variant="outlined"
              width="100%"
              height="46px"
              borderRadius="12px"
              onClick={() => { setModalMode("reopen"); setOpenModal(true); }}
              sx={{ textTransform: "none", fontSize: "15px" }}
            >
              Reopen Ticket
            </OutlinedPrimaryButton>
          )}
          {displayStatus !== "Closed" && displayStatus !== "Resolved" &&
            (isCallbackAllowedForCategory ? (
              <>
                <OutlinedPrimaryButton
                  variant="outlined"
                  width="100%"
                  height="46px"
                  borderRadius="12px"
                  disabled={!isCallbackUnlocked}
                  onClick={() => { setModalMode("callback"); setOpenModal(true); }}
                  sx={{ textTransform: "none", fontSize: "15px" }}
                >
                  Request a Callback
                </OutlinedPrimaryButton>
                {!isCallbackUnlocked && (
                  <Typography variant="caption" sx={CALLBACK_NOTICE_SX}>
                    You can request a callback starting <b>{formatIstDate(callbackUnlockDate)}</b>.{" "}
                    {URGENT_ASSISTANCE_LINE}
                  </Typography>
                )}
              </>
            ) : (
              // The button is dropped rather than disabled: a complaint never becomes eligible,
              // so a greyed-out control would only invite repeated clicks.
              <Typography variant="caption" sx={CALLBACK_NOTICE_SX}>
                {/* Callback requests are not available for complaints. {URGENT_ASSISTANCE_LINE} */}
              </Typography>
            ))}
          {displayStatus === "Pending Information" && (
            <OutlinedPrimaryButton
              variant="outlined"
              width="100%"
              height="46px"
              borderRadius="12px"
              onClick={() => { setModalMode("additionalDetails"); setOpenModal(true); }}
              sx={{ mt: 2, textTransform: "none", fontSize: "15px" }}
            >
              Submit additional details
            </OutlinedPrimaryButton>
          )}
        </Grid>
      </Grid>

      {/* Callback / Reopen Modal */}
      <ModalPopup
        isOpen={openModal}
        onClose={handleClose}
        maxWidth="xs"
        title={
          modalMode === "reopen"
            ? "Reopen Ticket"
            : modalMode === "additionalDetails"
            ? "Submit Additional Details"
            : "Request a Callback"
        }
        onNavigate={function (): void {
          throw new Error("Function not implemented.");
        }}
      >
        <Box component="form" onSubmit={handleCallbackSubmit}>
          {modalMode !== "additionalDetails" && (
            <Box sx={{ mb: 3 }}>
              <SelectOptions
                options={reasonOptions}
                label="Reason / Rationale"
                value={reason}
                isMandatory={true}
                onChange={(val) => setReason(val.toString())}
              />
            </Box>
          )}
          {/* Callback only: who to ring, and when. Both are required to submit. */}
          {isCallbackMode && (
            <>
              <label className="text-lable" htmlFor="callback-phone">
                Phone Number <span style={{ color: "red" }}>*</span>
              </label>
              <Box sx={{ mt: 0.5 }}>
                <PhoneInput
                  country={CALLBACK_DEFAULT_COUNTRY}
                  value={callbackPhone}
                  enableSearch
                  disableSearchIcon
                  countryCodeEditable={false}
                  searchPlaceholder="Search country"
                  placeholder={isIndianNumber ? "+91 98765 43210" : "Phone number"}
                  inputProps={{ id: "callback-phone", name: "callbackPhone" }}
                  inputStyle={{
                    ...PHONE_INPUT_STYLE,
                    ...(nationalPhone && !isPhoneValid ? { borderColor: "#d32f2f" } : {}),
                  }}
                  buttonStyle={PHONE_BUTTON_STYLE}
                  dropdownStyle={{ fontSize: "12px", textAlign: "left" }}
                  onChange={(value, data) => {
                    setCallbackPhone(value);
                    // `data` is {} until a country is resolved, so keep the previous dial code.
                    const country = data as { dialCode?: string };
                    if (country?.dialCode) setCallbackDialCode(country.dialCode);
                  }}
                />
              </Box>
              {/* Only surfaces once something invalid has been typed — no standing hint. */}
              {!!nationalPhone && !isPhoneValid && (
                <FormHelperText error sx={CALLBACK_HELPER_SX}>
                  {isIndianNumber
                    ? "Enter a valid 10-digit mobile number"
                    : "Enter a valid phone number"}
                </FormHelperText>
              )}

              <Box sx={{ mt: 2 }}>
                <label className="text-lable" htmlFor="callback-date">
                  Preferred Calling Date &amp; Time <span style={{ color: "red" }}>*</span>
                </label>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 140px", minWidth: 0 }}>
                  <TextareaField
                    id="callback-date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    padding="0px"
                    value={callbackDate}
                    // Earliest bookable day in IST — tomorrow, never today — whatever the
                    // browser's own zone. A typed-in date bypasses `min`, so the error below
                    // and isCallbackScheduleValid re-check it rather than trusting the picker.
                    inputProps={{ min: minCallbackDate }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCallbackDate(e.target.value)
                    }
                    error={!!callbackDate && callbackDate < minCallbackDate}
                    sx={CALLBACK_FIELD_SX}
                  />
                </Box>
                <Box sx={{ flex: "1 1 140px", minWidth: 0 }}>
                  <SelectOptions
                    // Every slot is bookable: the earliest date on offer is tomorrow, so none of
                    // them can be in the past.
                    options={CALLBACK_SLOTS.map(({ value, label }) => ({ value, label }))}
                    label=""
                    placeholder="Select Time Slot"
                    isMandatory={false}
                    value={callbackSlot}
                    onChange={(val) => setCallbackSlot(val.toString())}
                  />
                </Box>
              </Box>
              <FormHelperText sx={{ ...CALLBACK_HELPER_SX, mb: 2.5 }}>
                Working hours: 10:00 AM – 7:00 PM IST. Earliest available date is{" "}
                {formatIstDate(minCallbackDate)}.
              </FormHelperText>
            </>
          )}

          <Typography variant="body2" sx={{ mb: 1, color: "#5A5A75", fontWeight: 500 }}>
            Additional Context <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextareaField
            InputLabelProps={{ shrink: true }}
            highlightRed={true}
            placeholder="Type here..."
            variant="outlined"
            fullWidth
            required
            multiline
            padding="7px 14px 7px 14px"
            rows={3}
            value={context}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => handleChange(event)}
            error={!!contextError}
            sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
          {/* Message on the left, counter on the right, so the counter keeps its position
              whether or not the field is in error. */}
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, mt: 1 }}>
            <FormHelperText error sx={{ m: 0 }}>
              {contextError}
            </FormHelperText>
            <Typography
              variant="caption"
              textAlign="right"
              sx={{ flexShrink: 0, color: contextError ? "#d32f2f" : "#7A7A95" }}
            >
              {trimmedContext.length} / {maxCharacters} Characters (Min {minCharacters} required)
            </Typography>
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Typography variant="body2" sx={{ mb: 1, color: "#5A5A75", fontWeight: 500 }}>
              Attachment{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                (Optional · PDF, JPG, PNG · max {CB_MAX_SIZE_MB} MB)
              </Typography>
            </Typography>

            <Box
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleAddCallbackFiles(e.dataTransfer.files); }}
                sx={{
                  border: "1.5px dashed #d0d0d0",
                  borderRadius: 2,
                  p: 1.5,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: "#fafafa",
                  "&:hover": { borderColor: "#00B3B5", bgcolor: "#f0feff" },
                }}
              >
                <FileUploadOutlinedIcon sx={{ fontSize: 22, color: "#b0b0b0", mb: 0.25 }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  Click or drag &amp; drop to attach files
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  onChange={(e) => { handleAddCallbackFiles(e.target.files); e.target.value = ""; }}
                />
              </Box>

            {fileError && (
              <Alert severity="warning" sx={{ mt: 1, py: 0.25, fontSize: "0.78rem" }}>
                {fileError}
              </Alert>
            )}

            {callbackFiles.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {callbackFiles.map(({ file, objectUrl }, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      py: 0.75,
                      px: 1,
                      borderRadius: 1.5,
                      border: "1px solid #eee",
                      mb: 0.75,
                      bgcolor: "#fff",
                    }}
                  >
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: "#00B3B5", flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </Typography>
                    <Tooltip title="View file" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => window.open(objectUrl, "_blank", "noopener,noreferrer")}
                        sx={{ color: "#00B3B5", p: 0.5 }}
                      >
                        <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove file" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveCallbackFile(i)}
                        sx={{ color: "#e53935", p: 0.5 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {submitError && (
            <Alert severity="error" sx={{ mt: 1.5, py: 0.25, fontSize: "0.78rem" }}>
              {submitError}
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "80%", margin: "10px auto" }}>
            <OutlinedPrimaryButton
              type="submit"
              variant="contained"
              width="100%"
              backgroundColor="#00b3b5"
              textColor="#fff"
              disabled={submitting || !isContextValid || !isCallbackDetailsValid}
            >
              {submitting ? "Submitting…" : "Submit"}
            </OutlinedPrimaryButton>
          </Box>
        </Box>
      </ModalPopup>
    </Box>
  );
};

export default TicketDetails;